import { useEffect, useState } from 'react';

import { QuestionnaireIntroWrapperProps } from 'features/patient/assessment/assessment-controller/_components/questionnaire-intros';
import { useReducer } from 'lib/hooks/useReducer';

import {
  ImpactSliceReducer,
  initialImpactSliceLoad,
  initialState
} from './impactAPISlice';

import { Skeleton } from 'antd';
import { Text } from 'components/mvp-typography';
import ImpactLogo from 'img/impact-logo.svg';
import { useGetPendingAssessmentData } from 'lib/hooks/tanstack/use-getPendingAssessmentData';
import { useAppSelector } from 'lib/store/rootReducer';
import { QuestionnaireDataProps } from '../../assessment-controller/types';
import { ImpactQuestionnaireIntro } from './_components/impact-intro';
import { PassportIdInput } from './_components/input-passportId';

export const impactData: QuestionnaireDataProps = {
  imgLogo: ImpactLogo,
  header: {
    title: 'Neurocognitive Screening Tool'
  },
  subtitle: ''.toUpperCase(),
  title: 'Neurocognitive Screening Tool',
  description: [
    'ImPACT is a computerized test that measures memory, reaction time, attention span, visual and verbal problem-solving.',
    `Once you begin this test, you can't take a break until it's completed. Make sure to turn off your phone, use the washroom, and tell others you can't be disturbed before beginning the test.`
  ].join(' '),
  duration: '20-30 minutes'
};

const ActionLink = (props: { text: string; onClick: () => void | any }) => (
  <a
    onClick={e => {
      e.preventDefault();
      props.onClick();
    }}
    style={{
      color: '#230056',
      fontWeight: 'bold',
      textDecoration: 'underline'
    }}
  >
    {props.text}
  </a>
);

export function ImpactQuestionnaire(props: QuestionnaireIntroWrapperProps) {
  const user = useAppSelector(state => state.auth.user);
  const [impactInfo, dispatch] = useReducer(ImpactSliceReducer, initialState);

  const [insertImpactPassportId, setInsertImpactPassportId] = useState(false);
  const [passportId, setPassportId] = useState<string | null>(null);
  const pendingAssessmentOverview = useGetPendingAssessmentData(
    props.patientId
  );

  useEffect(() => {
    if (!user || !pendingAssessmentOverview.data) return;
    if (pendingAssessmentOverview.data.status === false) {
      throw new Error(
        `[ImpactQuestionnaire] Expected overview data to be available for Test-Taker (id=${props.patientId})`
      );
    }

    dispatch(
      initialImpactSliceLoad({
        patientId: user.uid,
        assessmentType: pendingAssessmentOverview.data.overview.assessmentType
      })
    );
  }, [user, props.patientId, pendingAssessmentOverview.data]);

  if (!user || impactInfo.isLoading) return <Skeleton active />;

  /**
   * Show a prompt to set the ImPACT passport ID
   */
  if (insertImpactPassportId) {
    return (
      <ImpactQuestionnaireIntro
        impactData={{
          ...impactData,
          title: 'Insert your ImPACT Passport ID',
          description: (
            <>
              <ActionLink
                text={"Don't have a passport ID? Click here"}
                onClick={() => {
                  setInsertImpactPassportId(false);
                }}
              />
              <PassportIdInput setValue={setPassportId} />
            </>
          )
        }}
        impactInfo={impactInfo}
        userId={props.patientId}
        existingPassportId={passportId}
      />
    );
  }

  return (
    <>
      <ImpactQuestionnaireIntro
        impactData={{
          ...impactData,
          description: (
            <>
              {impactData.description}
              {
                /**
                 * If the users impact info is not verified, then allow the user to input a custom ImPact ID
                 */
                !impactInfo.verified && (
                  <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                    <Text style={{ fontSize: '20px', fontWeight: 'bold' }}>
                      Have you already taken the ImPACT test before?
                    </Text>
                    <Text>
                      <ActionLink
                        text={'Click Here'}
                        onClick={() => {
                          setInsertImpactPassportId(true);
                        }}
                      />
                    </Text>
                  </div>
                )
              }
            </>
          )
        }}
        impactInfo={impactInfo}
        userId={props.patientId}
        existingPassportId={impactInfo.passportId ?? -1}
      />
    </>
  );
}
